"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sparkles, Play } from "lucide-react";
import { useState, useEffect } from "react";
import { LeadForm } from "@/components/ui/lead-form";
import HeroVideoDialog from "@/components/magicui/hero-video-dialog";

export function HeroCTA() {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [displayText, setDisplayText] = useState("");

  useEffect(() => {
    const words = ["Images", "Videos"];
    let wordIndex = 0;
    let charIndex = 0;
    let isDeleting = false;
    let timeoutId: NodeJS.Timeout;

    const type = () => {
      const currentWord = words[wordIndex];

      if (!isDeleting) {
        // Typing phase
        setDisplayText(currentWord.substring(0, charIndex + 1));
        charIndex++;

        if (charIndex === currentWord.length) {
          // Word complete, wait 5 seconds then start deleting
          timeoutId = setTimeout(() => {
            isDeleting = true;
            type();
          }, 5000);
          return;
        }

        // Continue typing
        timeoutId = setTimeout(type, 100);
      } else {
        // Deleting phase
        setDisplayText(currentWord.substring(0, charIndex - 1));
        charIndex--;

        if (charIndex === 0) {
          // Word deleted, move to next word
          isDeleting = false;
          wordIndex = (wordIndex + 1) % words.length;
          timeoutId = setTimeout(type, 500); // Small pause before next word
          return;
        }

        // Continue deleting
        timeoutId = setTimeout(type, 50);
      }
    };

    // Start the effect
    timeoutId = setTimeout(type, 1000); // Initial delay

    // Cleanup
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  return (
    <>
      <div className="space-y-6 sm:space-y-8 text-center lg:text-left">
        <div>
          <Badge variant="secondary" className="w-fit mx-auto lg:mx-0">
            <Sparkles className="w-4 h-4 mr-2" />
            AI-Powered Model Photography
          </Badge>
        </div>

        <div className="space-y-4 sm:space-y-6">
          <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tight">
            Transform Your Jewelry with
          </h1>
          <div className="flex justify-center lg:justify-start items-center">
            <span className="text-white text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tight">
              AI Model <span className="text-white">{displayText}</span>
            </span>
            <span className="bg-white w-1 h-8 sm:h-10 lg:h-12 ml-1 animate-pulse"></span>
          </div>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-lg mx-auto lg:mx-0">
            Create breathtaking model images and videos for your jewelry in
            minutes—no studios, no models, no shoots. Just flawless,
            professional results, instantly.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
          <Button
            size="lg"
            onClick={() => setIsFormOpen(true)}
            className="text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 bg-foreground text-background hover:bg-foreground/70"
            aria-label="Join waitlist for PhotoZoot"
          >
            Join Waitlist
          </Button>

          <Button
            variant="outline"
            size="lg"
            onClick={() => setIsVideoOpen(true)}
            className="text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6"
            aria-label="Watch demo video"
          >
            <Play className="w-4 sm:w-5 h-4 sm:h-5 mr-2" />
            Watch Demo
          </Button>
        </div>
      </div>

      <LeadForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        title="Join PhotoZoot Waitlist"
        buttonText="Submit & Join Waitlist"
      />

      <HeroVideoDialog
        videoSrc="https://www.youtube.com/embed/biBwLiClmIQ?si=2NzI8TomfxRb9LFi&autoplay=1"
        animationStyle="from-bottom"
        isOpen={isVideoOpen}
        onClose={() => setIsVideoOpen(false)}
      />
    </>
  );
}
